import {useMemo} from 'react';
import {useNavigate} from 'react-router';
import {Body1, Body2} from '@ui/atoms/typography';
import {BeamLogo} from '@ui/atoms/icons/beam-logo';
import CommentIcon from '@ui/atoms/icons/comment-icon';
import {ActivityIcon, MoreVerticalIcon} from '@ui/atoms/icons';
import {DropdownVertical} from '@ui/molecules/dropdown-vertical';
import {DropdownItem} from '@pages/dashboard/you/you-page.styles';
import {ItemLoader} from '@ui/organisms/item-loader';
import {NotificationEvent} from './notification.types';
import {
  formatNotificationTime,
  groupNotifications,
  NotificationGroupLabel,
} from './notification.utils';

interface NotificationListProps {
  notifications: NotificationEvent[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onOpen?: () => void;
  onMarkRead: (notification: NotificationEvent) => void;
  onMarkUnread: (notification: NotificationEvent) => void;
  onDismiss: (notification: NotificationEvent) => void;
}

const NotificationIcon = ({
  notification,
}: {
  notification: NotificationEvent;
}) => (
  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-backgroundDark text-purple300">
    {notification.kind === 'checkinComment' ||
    notification.kind === 'checkinMention' ? (
      <CommentIcon />
    ) : notification.kind === 'goal' ||
      notification.kind === 'goalComment' ||
      notification.kind === 'goalMention' ||
      notification.kind === 'actionItem' ||
      notification.kind === 'actionItemMention' ||
      notification.kind === 'actionItemOverdue' ? (
      <ActivityIcon />
    ) : (
      <BeamLogo />
    )}
  </div>
);

const NotificationRow = ({
  notification,
  onOpen,
  onMarkRead,
  onMarkUnread,
  onDismiss,
}: Omit<
  NotificationListProps,
  'notifications' | 'isLoading' | 'isError' | 'onRetry'
> & {
  notification: NotificationEvent;
}) => {
  const navigate = useNavigate();
  const openNotification = () => {
    if (notification.unread) onMarkRead(notification);
    navigate(notification.href);
    onOpen?.();
  };

  return (
    <article
      className={`group relative flex gap-3 border-b border-borderLight px-4 py-4 transition-colors sm:px-6 ${
        notification.unread ? 'bg-backgroundLight' : 'bg-white'
      } hover:bg-backgroundDark`}>
      <button
        type="button"
        onClick={openNotification}
        className="flex min-w-0 flex-1 gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-purple300">
        <NotificationIcon notification={notification} />
        <span className="min-w-0 flex-1">
          <span className="flex items-start gap-2">
            <Body2 kind="textDark" weight="semibold" className="flex-1">
              {notification.title}
            </Body2>
            {notification.unread && (
              <span
                className="mt-2 h-2 w-2 shrink-0 rounded-full bg-purple300"
                aria-label="Unread"
              />
            )}
          </span>
          {notification.body && (
            <Body2 kind="textBody" className="mt-1 line-clamp-2">
              {notification.body}
            </Body2>
          )}
          {notification.createdAt && (
            <Body2 kind="textMedium" size="11px" className="mt-2">
              {formatNotificationTime(notification.createdAt)}
            </Body2>
          )}
        </span>
      </button>
      <div className="shrink-0 pt-1">
        <DropdownVertical
          buttonAriaLabel={`Notification actions for ${notification.title}`}
          collapseOnClick
          customIcon={<MoreVerticalIcon />}
          dropdownWrapperStyle={{right: 0}}
          menu={(handleClose: () => void) => (
            <div className="flex min-w-[150px] flex-col">
              <DropdownItem
                type="button"
                onClick={() => {
                  notification.unread
                    ? onMarkRead(notification)
                    : onMarkUnread(notification);
                  handleClose();
                }}>
                Mark as {notification.unread ? 'read' : 'unread'}
              </DropdownItem>
              <DropdownItem
                type="button"
                onClick={() => {
                  onDismiss(notification);
                  handleClose();
                }}>
                Dismiss
              </DropdownItem>
            </div>
          )}
        />
      </div>
    </article>
  );
};

export const NotificationList = ({
  notifications,
  isLoading,
  isError,
  onRetry,
  onOpen,
  onMarkRead,
  onMarkUnread,
  onDismiss,
}: NotificationListProps) => {
  const groups = useMemo(() => groupNotifications(notifications), [
    notifications,
  ]);

  if (isLoading && !notifications.length) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <ItemLoader />
      </div>
    );
  }

  if (isError && !notifications.length) {
    return (
      <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 px-8 text-center">
        <Body1 kind="textDark" weight="semibold">
          We couldn’t load your notifications
        </Body1>
        <Body2 kind="textBody">Check your connection and try again.</Body2>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-[10px] border border-borderDark px-4 py-2 text-sm font-medium text-blackTwo">
          Try again
        </button>
      </div>
    );
  }

  if (!notifications.length) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 px-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-backgroundDark">
          <BeamLogo />
        </div>
        <Body1 kind="textDark" weight="semibold">
          You’re all caught up
        </Body1>
        <Body2 kind="textBody">
          New check-ins, comments, mentions, action items, and updates will
          appear here.
        </Body2>
      </div>
    );
  }

  return (
    <div>
      {(['Today', 'Yesterday', 'Earlier'] as NotificationGroupLabel[]).map(
        (label) =>
          groups[label].length ? (
            <section key={label} aria-labelledby={`notifications-${label}`}>
              <div className="border-b border-borderLight bg-white px-4 py-3 sm:px-6">
                <Body2
                  id={`notifications-${label}`}
                  kind="textBody"
                  weight="semibold">
                  {label}
                </Body2>
              </div>
              {groups[label].map((notification) => (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  onOpen={onOpen}
                  onMarkRead={onMarkRead}
                  onMarkUnread={onMarkUnread}
                  onDismiss={onDismiss}
                />
              ))}
            </section>
          ) : null,
      )}
    </div>
  );
};
